import React, { useEffect, useMemo, useState } from "react";
import s from "./createFlow.module.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  globalDataSelector,
  setAdaptiveMenu,
} from "../../../redux/slices/globalDataSlice";
import { Link, useNavigate } from "react-router-dom";
import useCustomSnackbar from "../../../hooks/useCustomSnackbar";
import { setTopUpBalanceActive } from "../../../redux/slices/financeSlice";
import SvgSelector from "../../../components/SvgSelector/SvgSelector";
import {
  createFlow,
  fetchDomainZones,
  flowSettingsSelector,
  setSubDomain,
} from "../../../redux/slices/flowSettingsSlice";
import { useFormik } from "formik";
import {
  snackbar_error,
  snackbar_warning,
} from "../../../utils/vars/staticVariables";
import * as Yup from "yup";
import ReactSelectTheme from "../../../components/ReactSelectTheme/ReactSelectTheme";
import PrimaryBtn from "../../../components/UiKit/Buttons/PrimaryBorderBtn/PrimaryBorderBtn";
import ConfirmPopup from "../../../components/UiKit/Popups/ConfirmPopup/ConfirmPopup";
import { authSelector } from "../../../redux/slices/authSlice";
import ConfirmCreateFlowPopup from "./ConfirmCreateFlowPopup/ConfirmCreateFlowPopup";
import {
  cannotBuyFlowWithoutSubscriptionInTeam,
  flowCreateLimit,
  noDomains,
  notEnoughMoney,
} from "../../../utils/vars/errorMessages";
import { useTranslation } from "react-i18next";
import {
  noDomainsNotifError,
  unsufficientFundsNotifError,
} from "../../../utils/vars/notificationsMessages";
import LanguageDropdown from "../../../components/LanguageDropdown/LanguageDropdown";
import {is_tech_user} from "../../../storage/user";
import {managementSelector} from "../../../redux/slices/managementSlice";
import Header from "../../../components/Partials/Header/Header";
import {getLanguage} from "../../../storage/switchLanguage";
import {setFreeDomainsCount, subscriptionSelector} from "../../../redux/slices/subscriptionSlice";

const CreateFlow = () => {
  const [isConfirmPopupActive, setConfirmPopupActive] = useState(false);
  const [isPopupLimitActive, setPopupLimitActive] = useState(false);
  const { isCollapsedSidebar, isAdaptiveMenuActive } =
    useSelector(globalDataSelector);

  const { domainZonesList, createFlowPending, setSubDomainPending } =
    useSelector(flowSettingsSelector);
  const { isPartner } = useSelector(authSelector);
  const { standardClickPriceValue } = useSelector(managementSelector);
  const {fetchSubscriptionsPending, freeDomainsCount} = useSelector(subscriptionSelector);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const showSnackbar = useCustomSnackbar();

  const { t } = useTranslation();

  useEffect(() => {
    dispatch(fetchDomainZones());
  }, [dispatch]);

  const domainsZoneListOptions = useMemo(() => {
    return domainZonesList?.map((domainZone) => ({
      value: domainZone,
      label: domainZone,
    }));
  }, [domainZonesList]);

  const formik = useFormik({
    initialValues: {
      domainZoneValue: null,
      subDomainValue: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      subDomainValue: Yup.string()
        .matches(/^\S*$/, t("validation_no_space_between_characters"))
        .matches(/^[a-zA-Z0-9]+$/, t("validation_symbols_and_digits"))
        .min(2, t("validation_min_2_letters"))
        .max(20, t("validation_max_20_letters")),
    }),
    onSubmit: async (form) => {
      const createFlowResult = await dispatch(createFlow(form.domainZoneValue));
      const { status, data } = createFlowResult?.payload;
      if (status === 200) {
        dispatch(setFreeDomainsCount(freeDomainsCount - 1))
        if (formik.values.subDomainValue) {
          const subDomainObj = {
            flow: data?.flow?.id,
            address: form.subDomainValue.trim().toLowerCase(),
          };

          const setSubDomainResults = await dispatch(
            setSubDomain(subDomainObj),
          );

          if (setSubDomainResults?.payload?.status === 201) {
            navigate(`/my-flows/edit/${data?.flow?.id}`);
          } else {
            showSnackbar(t("failed_to_create_subdomain"), snackbar_error);
          }
        } else {
          navigate(`/my-flows/edit/${data?.flow?.id}`);
        }
      } else {
        if (data.detail === cannotBuyFlowWithoutSubscriptionInTeam) {
          showSnackbar(t('cannotBuyFlowWithoutSubscriptionInTeam'), snackbar_warning);
        }
        if (data.detail === noDomains) {
          showSnackbar(t(noDomainsNotifError), snackbar_warning);
        }
        if (data.detail === notEnoughMoney) {
          showSnackbar(t(unsufficientFundsNotifError), snackbar_warning);
        }
        if (data.detail === flowCreateLimit) {
          setPopupLimitActive(true);
        }
      }
    },
    validateOnMount: true,
    validateOnBlur: true,
  });

  return (
    <div
      className={`${s.createFlowWrapper} ${
        isCollapsedSidebar === "true" ? s.collapsedSidebar : ""
      }`}
    >
      <ConfirmPopup
        icon={<SvgSelector id="flow-popup-icon" />}
        active={isPopupLimitActive}
        setActive={setPopupLimitActive}
        action={() => navigate("/my-flows")}
        confirmBtnText={t("continue")}
        title={t("create_flow_limit_title")}
        description={
          <div className={s.support}>
            {`${t("duplicate_limit_message")}. ${t("contact")}`}
            <a
              href="https://t.me/TL_supp_wwa_vados"
              target="_blank"
              rel="noreferrer"
            >
              {t("supportPopupMessage")}
            </a>
          </div>
        }
      />
      <Header/>

      <div className={s.pageContent}>
        <div className={s.pageMarking}>
          <Link to="/my-flows" className={s.link}>
            {t("flows")}
          </Link>
        </div>
        <div className={s.switchPageContentContainer}>
          <div className={s.buttonsContainer}>
            <button className={`${s.pageBtn} ${s.active} ${s.hardCoded}`}>
              <SvgSelector id="domain-select-icon" />
              {t("domain_placeholder")}
            </button>

            <button className={`${s.pageBtn} ${s.disabled}`}>
              <SvgSelector id="os-select-icon" />
              {t("settings")}
            </button>

            <button className={`${s.pageBtn} ${s.disabled}`}>
              <SvgSelector id="preland-indicator-icon" />
              {t("prelanding")}
            </button>

            <button className={`${s.pageBtn} ${s.disabled}`}>
              <SvgSelector id="redirect-indicator-icon" />
              {t("redirector")}
            </button>
          </div>
        </div>
        <div className={s.domainInfoWrapper}>
          <div className={s.domainInfo}>
            <SvgSelector id="check-primary-circle" />
            {t("provided_domain")}
          </div>
        </div>
        <form className={s.form} onSubmit={formik.handleSubmit}>
          <ConfirmCreateFlowPopup
            icon={<SvgSelector id="flow-popup-icon" />}
            active={isConfirmPopupActive}
            setActive={(value) => setConfirmPopupActive(value)}
            action={() => formik.handleSubmit()}
            confirmBtnText={t("create")}
            title={t("flow_creation")}
            description={
              isPartner
                ? t("create_flow_confirm")
                  : getLanguage() === 'en' ? `If you create the flow, a ${freeDomainsCount > 0 ? '$0' : '$6'} fee will be deducted for the domain`
                      : `${t("create_flow_write_off_confirm")} ${freeDomainsCount > 0 ? '$0' : '$6'} ${t('for_domain')}`
            }
            forcedClose={false}
          />
          <div className={s.formFieldsWrapper}>
            <div className={s.fieldWrapper}>
              <p className={s.fieldName}>
                {t("domain_zone")}{" "}
                <span>{t("not_required_input_tooltip")}</span>
              </p>
              <ReactSelectTheme
                isClearable={true}
                darkTheme={true}
                options={domainsZoneListOptions}
                placeholder={t("choose_option_placeholder")}
                value={formik.values.domainZoneValue}
                action={(selectedOption) =>
                  formik.setFieldValue("domainZoneValue", selectedOption)
                }
              />
              <p className={s.fieldName}>
                <span>{t("select_domain_zone_desc")}</span>
              </p>
            </div>

            <div className={s.fieldWrapper}>
              <p className={s.fieldName}>
                {t("sub_domain")} <span>{t("not_required_input_tooltip")}</span>
              </p>
              <input
                type="text"
                name="subDomainValue"
                className={`${s.textInput} ${
                  formik.touched.name && formik.errors.name ? s.error : ""
                }`}
                placeholder={t("placehoder_type_text")}
                value={formik.values.subDomainValue}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.subDomainValue && formik.errors.subDomainValue ? (
                <div className={s.error}>{formik.errors.subDomainValue}</div>
              ) : null}
            </div>
          </div>

          <div className={s.buttons}>
            <Link className={s.whiteBorderBtn} to="/my-flows">
              {t("button_cancel")}
            </Link>
            <PrimaryBtn
              action={() => {
                if (Object.keys(formik.errors).length === 0) {
                  setConfirmPopupActive(true);
                }
              }}
              text={t("create")}
              submit={false}
              pending={createFlowPending || setSubDomainPending || fetchSubscriptionsPending}
              className={s.customPrimaryBtn}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFlow;
